import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEventListeners, useEventManager } from '@deathbyjer/react-event-manager'
import { eventForId } from 'components/utility/draw_area'
import { calNewRectDuplicateEl, splitComponentKey, uniqueComponentKey } from '../../common/utility'
import { Tools } from '../../common/toolbar/Tools'
import Behaviors from 'components/form_builder/common/sidebars/right/Behaviors'
import Transform, { TransfromGroupFields } from 'components/form_builder/common/sidebars/right/Transform'
import { removeComponents, updatePosition, useComponentCreator, useDependentFieldCreator } from './componentSlice'
import Templating from '../../common/sidebars/right/Templating'
import clsx from 'clsx'
import SectionDivider from 'components/form_builder/common/sidebars/SectionDivider'
import { DependentFieldModal } from 'components/form_builder/modal/DependentFieldModal'
import { FillOnlyPaths } from 'components/form_builder/common/sidebars/right/Paths'
import Interactability, { MultipleInteractability } from 'components/form_builder/common/sidebars/right/Interactability'
import Visibility, { MultipleVisibility } from 'components/form_builder/common/sidebars/right/Visibility'
import Checkbox from 'components/form_builder/common/sidebars/right/Checkbox'
import PathSearch from 'components/form_builder/common/sidebars/right/PathSearch'
import { checkIncompleteField, useDataTemplate } from '../data/dataSlice'
import PathForMultipleFields from 'components/form_builder/common/sidebars/right/PathForMultipleFields'
import GroupSectionAssignment from 'components/form_builder/common/sidebars/right/GroupSectionAssignment'

const DELETE_KEY = [ 8, 46 ]

export const Highlight = ({ id, setId }) => {
  const key = uniqueComponentKey("highlight", id)
  const highlight = useSelector(state => state.components.all[key])
  const isNewest = useSelector(state => key == state.components.newest)

  const fullId = ["highlight", id]
  setId ? setId(fullId) : null

  useEventListeners({
    [eventForId("rect-updated", fullId)]: ({ rect }) => {
      console.log(rect)
    },
  })

  return <div style={{ background: highlight.color, height: "100%", opacity: .5 }}></div>
}

// Field Area
const FieldComponent = ({ id, type, subtype, setId, selected, rect }) => {
  const dispatch = useDispatch()
  const fullId = useMemo(() => [type, id, subtype].filter(Boolean), [type, id, subtype])
  const key = uniqueComponentKey(...fullId)
  const component = useSelector((state) => state.components.all[key])
  const template = useSelector((state) => state.data[type][id])
  const isIncompleteField = checkIncompleteField(type, template)
  const inputAreaRef = useRef()
  const [isTextOverflowing, setIsTextOverflowing] = useState(false)
  setId ? setId(fullId) : null

  useEventListeners({
    [eventForId('rect-updated', fullId)]: ({ rect }) => {
      dispatch(updatePosition({ key, rect }))
    },
  })

  useEffect(() => {
    if (!inputAreaRef.current) return false
    setIsTextOverflowing(
      inputAreaRef.current.scrollWidth > inputAreaRef.current.clientWidth ||
        inputAreaRef.current.scrollHeight > inputAreaRef.current.clientHeight
    )
  }, [rect?.width, rect?.height, component?.behaviorInputs?.placeholder])

  return (
    <>
      <div ref={inputAreaRef} style={{background: isIncompleteField ? '#E4F2F5' : ''}} className="input-area-field dome-d-flex dome-justify-center dome-flex-column dome-full-height">
        {type !== 'checkbox' && <p style={{visibility: isTextOverflowing ? 'hidden' : 'visible'}} >{component?.behaviorInputs?.placeholder}</p>}
      </div>
      {selected && subtype === 'signature' && <DependentFieldModal signatureComponent={component}  />}
    </>
  )
}

export const DataField = (props) => <FieldComponent {...props} type="data" />
export const CheckBoxField = (props) => <FieldComponent {...props} type="checkbox" />
export const SignatureFieldSignature = (props) => <FieldComponent {...props} type="signature" subtype="signature" />
export const SignatureFieldName = (props) => <FieldComponent {...props} type="signature" subtype="name" />
export const SignatureFieldTime = (props) => <FieldComponent {...props} type="signature" subtype="time" />

// Toolbars
const DependentFieldLabel = {
  time: 'date signed',
  name: 'full name',
}

export const SingleSignatureWithSingleDataToolbar = ({ components }) => {
  const dispatch = useDispatch()
  const createDependentFieldComponent = useDependentFieldCreator()

  const { key: dataKey, page, rect } = components.filter((c) => c.type == 'data')[0]
  const { id: signatureId } = components.filter((c) => c.type == 'signature')[0]

  const swapComponent = useCallback(
    (subtype) => {
      createDependentFieldComponent({id: signatureId, type: 'signature', page, rect, subtype})
      dispatch(removeComponents([dataKey]))
    },
    [dispatch, dataKey, page, rect, signatureId, createDependentFieldComponent]
  )

  const handleConvertToSignatureName = useCallback(() => swapComponent('name'), [swapComponent])
  const handleConvertToSignatureTime = useCallback(() => swapComponent('time'), [swapComponent])

  return (
    <div className="side-toolbar signature-w-data-toolbar scrollable">
      <button onClick={handleConvertToSignatureName}>convert to signature name</button>
      <button onClick={handleConvertToSignatureTime}>convert to signature time</button>
    </div>
  )
}

export const SignatureSinglePropertiesToolbar = ({ id, component }) => {
  const signatureKey = uniqueComponentKey(component.type, component.id, 'signature')
  const signatureComponent = useSelector((state) => state.components.all[signatureKey])

  return (
    <div key={id} className="side-toolbar signature-toolbar scrollable">
      <Behaviors id={id} component={component} showPlaceholder={false} showLabel={false} />
      <Transform
        id={splitComponentKey(signatureKey)}
        page={signatureComponent?.page}
        position={signatureComponent?.rect}
      />
      {Object.keys(DependentFieldLabel).map((subtype) => (
        <DependentSingleToolbar
          key={subtype}
          subtype={subtype}
          signatureComponent={signatureComponent}
          sectionName={DependentFieldLabel[subtype]}
        />
      ))}
    </div>
  )
}

export const SignatureSingleDataToolbar = ({ id, component }) => {
  return (
    <div key={id} className="side-toolbar signature-toolbar scrollable">
      <PathSearch/>
      <Templating id={id} component={component} />
    </div>
  )
}

const DependentSingleToolbar = ({ signatureComponent, subtype, sectionName }) => {
  const dispatch = useDispatch()
  const events = useEventManager()
  const { id, type, page, rect } = signatureComponent
  const createDependentFieldComponent = useDependentFieldCreator()
  const key = uniqueComponentKey(type, id, subtype)
  const component = useSelector((state) => state.components.all[key])

  const removeComponent = () => dispatch(removeComponents([key]))

  const addComponent = () => createDependentFieldComponent({ id, type, page, rect: calNewRectDuplicateEl({ rect, page }), subtype })

  return (
    <div>
      {component ? (
        <Transform
          page={component?.page}
          onAction={removeComponent}
          position={component?.rect}
          id={splitComponentKey(key)}
          sectionName={sectionName}
          actionName={component ? 'remove' : 'add'}
        />
      ) : (
        <SectionDivider
          onAction={addComponent}
          sectionName={sectionName}
          actionName={component ? 'remove' : 'add'}
        ></SectionDivider>
      )}
    </div>
  )
}

export const DataPropertiesToolbar = ({ id, component }) => {
  return (
    <div key={id} className="side-toolbar data-toolbar scrollable">
      <Behaviors id={id} component={component} />
      <Transform id={id} page={component?.page} position={component?.rect} />
    </div>
  )
}

export const DataDataToolbar = ({ id, component }) => {
  const settingElId = useId()
  const template = useDataTemplate(...id)
  const isIncompleteField = checkIncompleteField(component.type, template)

  return (
    <div key={id} style={{paddingBottom: 60}} className="side-toolbar data-toolbar scrollable">
      <PathSearch/>
      <Templating id={id} component={component} isIncompleted={isIncompleteField}/>
      <FillOnlyPaths id={id} component={component} />
      <Interactability component={component} />
      <Visibility component={component} />
    </div>
  )
}

export const CheckBoxPropertiesToolbar = ({ id, component }) => {
  return (
    <div key={id} className="side-toolbar data-toolbar scrollable">
      <Behaviors id={id} component={component} showPlaceholder={false} showSymbol={true} />
      <Transform id={id} page={component?.page} position={component?.rect} />
    </div>
  )
}

export const CheckBoxDataToolbar = ({ id, component }) => {
  return (
    <div key={id} className="side-toolbar data-toolbar scrollable">
      <PathSearch/>
      <Checkbox id={id} component={component} />
      <Interactability component={component} />
      <Visibility component={component} />
    </div>
  )
}

const GroupEditModeWarning = () => {
  const selectedKeys = useSelector(({components}) => components.selected)
  return (
    <div className='group-edit-mode-warning'>
      <div className='dome-d-flex dome-align-start dome-gap6'>
        <span>
          <i className="fa-light fa-object-group dome-color-darkest-grey"></i>
        </span>
        <div className='dome-d-flex dome-flex-column'>
          <span className='dome-p3 dome-color-darkest-grey'>group edit mode</span>
          <span className='dome-p3 dome-color-med-grey'>updating values for {selectedKeys.length} fields</span>
        </div>
      </div>
    </div>
  )
}
export const GroupFieldDataToolbar = ({ ids, components }) => {
  return (
    <div>
      <GroupEditModeWarning />
      <PathSearch/>
      <GroupSectionAssignment components={components} />
      <PathForMultipleFields id={ids} page={components[0]?.page} components={components} />
      <MultipleInteractability shareComponent={components[0]} components={components} />
      <MultipleVisibility shareComponent={components[0]} components={components} />
    </div>
  )
}

export const GroupFieldPropertiesToolbar = ({ ids, components }) => {
  return (
    <div>
      <GroupEditModeWarning />
      <TransfromGroupFields ids={ids} page={components[0]?.page} components={components} />
    </div>
  )
}